import cloneDeep from 'lodash/cloneDeep';
import pickBy from 'lodash/pickBy';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';

import {
    CampaignFilterLabels,
    CampaignFilterValues,
    filtersItemsInitial,
    FiltersName,
    IFilterItem,
    IFilterProp,
    ISelectedFilters,
    selectedFiltersInitial,
    SortByValues,
    sortedItemsInitial,
    TFilters,
    TypeFilterLabels,
} from '~/src/entities/filteredBundles';
import { ITariff } from '~/src/entities/formattedTariff';
import { TAppThunkDispatch, TGetState } from '~/src/store';
import { EFilteredBundlesTypes } from '~/src/store/reducers/filteredBundles';
import { selectAllTariffs, selectCheckedFilters, selectIsEmpty } from '~/src/store/selectors/filteredBundles';

type TFiltersMapped = Record<string, { name: string | number, label: string}>;

export const setInitialGroupKey = (groupKey: string) => {
    return (dispatch: TAppThunkDispatch): void => {
        dispatch({
            type: EFilteredBundlesTypes.SET_GROUP_KEY,
            payload: {
                groupKey,
            },
        });
    };
};

export const toggleFilterTabs = () => {
    return (dispatch: TAppThunkDispatch, getState: TGetState): void => {
        const { filteredBundles: { groupKey } } = getState();
        const [result] = ['city', 'village'].filter(key => key !== groupKey);
        dispatch({
            type: EFilteredBundlesTypes.SET_GROUP_KEY,
            payload: {
                groupKey: result,
            },
        });
    };
};

export const toggleSelected = (name: string, value: string) => {
    return (dispatch: TAppThunkDispatch, getState: TGetState): void => {
        const { filteredBundles: { selected: selectedFilters, groupKey } } = getState();
        const typedValue = +value || value;

        let action = '';

        const selected = [...selectedFilters];
        const currentFilterIndex = selected.findIndex(
            filter => filter.group === groupKey && filter.name === name,
        );

        const isValueExists = selected[currentFilterIndex].values.includes(typedValue);
        if (isValueExists) {
            action = 'remove filter';
            selected[currentFilterIndex].values = selected[currentFilterIndex].values.filter(
                f => f !== typedValue,
            );
        } else {
            action = 'add filter';
            selected[currentFilterIndex].values.push(typedValue);
        }
        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.tariffCards,
            action,
            label: `${name} ${value}`,
        });
        dispatch({
            type: EFilteredBundlesTypes.SET_SELECTED,
            payload: {
                selected,
            },
        });
    };
};

export const applyInitialFilters = (props: IFilterProp) => {
    return (dispatch: TAppThunkDispatch, getState: TGetState): void => {
        const { filteredBundles: { selected: selectedFilters, groupKey } } = getState();
        const selected = [...selectedFilters];
        for (const name in props) {
            const currentFilterIndex = selected.findIndex(
                filter => filter.group === groupKey && filter.name === name,
            );
            if (props[name] !== undefined && currentFilterIndex !== -1) {
                if (typeof props[name] === 'object') {
                    // @ts-ignore
                    selected[currentFilterIndex].values = props[name].map(value => +value || value);
                } else {
                    // @ts-ignore
                    selected[currentFilterIndex].values.push(+props[name] || props[name]);
                }
            }
        }

        dispatch({
            type: EFilteredBundlesTypes.SET_SELECTED,
            payload: {
                selected,
            },
        });
    };
};

export const sortBy = (by: SortByValues) => {
    return (dispatch: TAppThunkDispatch, getState: TGetState): void => {
        const { filteredBundles: { sortedItems } } = getState();

        const _sortedItems = sortedItems && {
            ...sortedItems,
            items: [
                ...sortedItems?.items.map((item: IFilterItem) => ({
                    ...item,
                    checked: item.value === by,
                })),
            ],
        };

        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.tariffCards,
            action: 'sort',
            label: by,
        });
        dispatch({
            type: EFilteredBundlesTypes.SET_SORTBY,
            payload: {
                sortBy: by,
                sortedItems: _sortedItems,
            },
        });
    };
};

export const reset = () => {
    return (dispatch: TAppThunkDispatch): void => {
        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.tariffCards,
            action: 'reset filter',
            label: '',
        });
        dispatch({
            type: EFilteredBundlesTypes.RESET_SELECTED,
            payload: {
                selected: cloneDeep(selectedFiltersInitial),
                sortBy: SortByValues.recommended,
            },
        });
    };
};

const sortByLine = (tariffs: ITariff[]) => {
    return [...tariffs].sort((a, b) => a.tariffLine?.sort - b.tariffLine?.sort);
};

const checkedForFilters = (selectedFilters: ISelectedFilters[], groupKey: string) => {
    return (
        items: IFilterItem[],
        name: string,
        value: string | number,
        label: string,
    ) => {
        const _items = [...items];

        _items.push({
            value,
            label,
            checked: !!selectedFilters.find(filter => (
                filter.group === groupKey &&
                filter.name === name &&
                filter.values.includes(value)
            )),
        });
        if (name === FiltersName.speed) {
            _items.sort((a, b) => +a.value - +b.value);
        }
        return _items;
    };
};

const checkedForCampaign = (selectedFilters: ISelectedFilters[], groupKey: string) => {
    return (items: IFilterItem[], name: string, filterName: string) => {
        const _items = [...items];

        if (!_items.some(filter => filter.value === CampaignFilterValues[filterName])) {
            const itemValue = CampaignFilterValues[filterName];
            const itemLabel = CampaignFilterLabels[filterName];

            _items.push({
                value: itemValue,
                label: itemLabel,
                checked: !!selectedFilters.find(filter => (
                    filter.group === groupKey &&
                    filter.name === name &&
                    filter.values.includes(itemValue)
                )),
            });
        }
        return _items;
    };
};

export const getAllItems = () => {
    return (dispatch: TAppThunkDispatch, getState: TGetState): void => {
        const state = getState();
        const { filteredBundles: { selected: selectedFilters, groupKey } } = state;
        const tariffs = selectAllTariffs(state);

        const filtersItems = cloneDeep(filtersItemsInitial);
        const sortedItems = cloneDeep(sortedItemsInitial);

        const checkVillageTabDisabling: (number | boolean)[] = [];

        const getCheckedForFilters = checkedForFilters(selectedFilters, groupKey);
        const getCheckedForCampaign = checkedForCampaign(selectedFilters, groupKey);

        const allItems = sortByLine(tariffs)
            .reduce((acc: TFilters, tariff) => {
                const { speed, tariffLine, type, isCottage } = tariff;
                const targetGroup = isCottage ? 'village' : 'city';

                acc[targetGroup].items.forEach(item => {
                    checkVillageTabDisabling.push(isCottage);
                    const { name } = item;

                    if (name !== FiltersName.campaign) {
                        if (!item.items.some(elem => {
                            // @ts-ignore
                            return elem.value === tariff[name];
                        })) {
                            const filtersMapped: TFiltersMapped = {
                                [FiltersName.speed]: { name: speed, label: `${speed}  Мбит/с` },
                                [FiltersName.lineId]: { name: tariffLine?.id, label: tariffLine?.title },
                                [FiltersName.type]: { name: type, label: TypeFilterLabels[type] },
                            };
                            const { name: mappedName, label } = filtersMapped[name];
                            item.items = getCheckedForFilters(item.items, name, mappedName, label);
                        }
                    } else {
                        ['priceAction', 'equipment'].forEach(filterName => {
                            item.items = getCheckedForCampaign(item.items, name, filterName);
                        });
                    }
                });

                return acc;
            }, filtersItems);

        // TODO: необходим рефакторинг получения уникальных линеек
        allItems.city.items[1].items = [...new Map(allItems.city.items[1].items.map(item => [item.value, item])).values()];
        allItems.village.items[1].items = [...new Map(allItems.village.items[1].items.map(item => [item.value, item])).values()];

        const isVillageTabDisable = checkVillageTabDisabling.every(el => el == checkVillageTabDisabling[0]);

        dispatch({
            type: EFilteredBundlesTypes.SET_ITEMS,
            payload: {
                isVillageTabDisable,
                items: allItems,
                sortedItems,
            },
        });
    };
};

const sortTariffs = (sortingTariffs: ITariff[], sortTarget: SortByValues) => {
    const buffer = [...sortingTariffs];

    switch (sortTarget) {
        case SortByValues.recommended:
            buffer.sort((a, b) => a.weight - b.weight);
            break;
        case SortByValues.cheap:
            buffer.sort((a, b) => (a.priceAction ?? a.price) - (b.priceAction ?? b.price));
            break;
        case SortByValues.expensive:
            buffer.sort((a, b) => (b.priceAction ?? b.price) - (a.priceAction ?? a.price));
            break;
    }

    return buffer;
};

const getResultWithEmpty = (tariffs: ITariff[], sortBy: SortByValues) => {
    return (dispatch: TAppThunkDispatch, getState: TGetState): void => {
        const { filteredBundles: { groupKey } } = getState();
        const isVillage = groupKey === 'village';
        const localizedTariffs = [...tariffs].filter(tariff => !!tariff.isCottage === isVillage);
        dispatch({
            type: EFilteredBundlesTypes.SET_TARRIFS,
            payload: {
                tariffs: sortTariffs(localizedTariffs, sortBy),
            },
        });
    };
};

const getResultsWithFilters = (tariffs: ITariff[], sortBy: SortByValues) => {
    return (dispatch: TAppThunkDispatch, getState: TGetState): void => {
        const state = getState();
        const activeSelectedFilters = selectCheckedFilters(state);

        const normalizeTariffs = tariffs.reduce((acc: Record<string, ITariff>, tariff) => {
            acc[tariff.productId] = tariff;
            return acc;
        }, {});

        const result = activeSelectedFilters.reduce((acc: Record<string, ITariff>, selectedFilter: ISelectedFilters) => {
            return pickBy(acc, (tariff) => {
                const isCity = selectedFilter.group === 'city';
                return !!tariff.isCottage === !isCity &&
                    (selectedFilter.name === FiltersName.campaign
                        // @ts-ignore
                        ? selectedFilter.values.some(value => !!tariff[value])
                            : selectedFilter.name === FiltersName.lineId
                            ? selectedFilter.values.includes(tariff.tariffLine.id)
                        // @ts-ignore
                        : selectedFilter.values.includes(tariff[selectedFilter.name])
                    );
            });
        }, normalizeTariffs);

        dispatch({
            type: EFilteredBundlesTypes.SET_TARRIFS,
            payload: {
                tariffs: sortTariffs(Object.values(result), sortBy),
            },
        });
    };
};

export const getResults = () => {
    return (dispatch: TAppThunkDispatch, getState: TGetState): void => {
        const state = getState();
        const { filteredBundles: { sortBy } } = state;
        const isEmptyFilters = selectIsEmpty(state);
        const tariffs = selectAllTariffs(state);

        if (!isEmptyFilters) {
            dispatch(getResultsWithFilters(tariffs, sortBy));
        } else {
            dispatch(getResultWithEmpty(tariffs, sortBy));
        }
    };
};
